/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import route_vs_component_splitImg from 'assets/images/route-vs-component-split.png'
import splitImg from 'assets/images/split.png'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Route-based и Component-based splitting</h2>
    <p className={styles.description}>
      Первый подход состоит в разбиении приложения на отдельные маршруты и загрузке каждого маршрута асинхронно. Этого
      вполне достаточно для большинства приложений, переходим на новый раут - загружается необходимый код для его
      обслуживания.{' '}
    </p>
    <p className={styles.description}>
      Но есть еще один вариант. Раут это просто компонент. Поэтому можно разделять код на уровне компонентов, а не
      маршрутов. Какая в этом выгода?{' '}
    </p>
    <p className={styles.description}>
      В действительно большом приложении - огромная. Интерфейсы могут быть очень громоздкими, и в тех местах, где
      модальные окна, вкладки, скрытые формы и т. п., не нужны до определенного действия пользователя, разделение кода
      помогает ускорить рендер страницы.
    </p>
    <img className={styles.img} src={route_vs_component_splitImg} alt="route-vs-component-split" />
    <p className={styles.description}>
      Это не значит что необходимо использовать один или другой подход, используйте оба. Делите и маршруты, и части
      страницы, это все просто компоненты. Как, что и где разделять это целиком ваше решение, зависящее от сложившейся
      ситуации.{' '}
    </p>
    <p className={styles.description}>
      Чрезмерный сплитинг, кстати, тоже не лучшая идея. HTTP-запрос за файлом может быть дольше чем добавленный вес к
      первой загрузке.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - asyncComponent HOC</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Функция <code className={styles.code}>asyncComponent</code> принимает объект настроек из двух свойств:{' '}
          <code className={styles.code}>loader</code> - функция, которая при вызове будет динамически импортировать
          компонент, <code className={styles.code}>loading</code> - компонент который будем показывать пока идет
          HTTP-запрос.
        </li>
        <li className={styles.list__item}>
          В <code className={styles.code}>componentDidMount</code> мы просто вызываем функцию{' '}
          <code className={styles.code}>loader</code>, и сохраняем динамически загруженный компонент в состояние.
        </li>
        <li className={styles.list__item}>
          После чего делаем рендер по условию, где рендерим загруженный компонент, если он уже загрузился. В противном
          случае рендерим компонент <code className={styles.code}>Loading</code>.
        </li>
      </ul>
      <CopyBlock
        text={`// asyncComponent.js

const asyncComponent = ({ loader, loading: Loading }) => {
  return class AsyncComponent extends Component {
    state = {
      component: null,
    };

    async componentDidMount() {
      const { default: component } = await loader();

      this.setState({ component });
    }

    render() {
      const { component: LoadedComponent } = this.state;

      return LoadedComponent ? (
        <LoadedComponent {...this.props} />
      ) : (
        <Loading />
      );
    }
  };
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Теперь используем эту функцию для динамической загрузки страниц. </p>
      <CopyBlock
        text={`const AsyncHome = asyncComponent({
  loader: () => import('./pages/Home'),
  loading: Loader,
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Важно понимать, что здесь не происходит импорта компонента. Мы передаем функцию которая будет использована для
        динамического импорта, когда будет создан компонент <code className={styles.code}>AsyncHome</code>.{' '}
      </p>
      <p className={styles.description}>
        Может показаться странным, что мы передаем функцию. Почему бы просто не передать строку{' '}
        <code className={styles.code}>'./pages/Home'</code>, а затем выполнить динамический импорт внутри{' '}
        <code className={styles.code}>asyncComponent</code>?
      </p>
      <p className={styles.description}>
        Это связано с тем, что мы хотим явно указать компонент, который мы динамически импортируем. Основываясь на этом,
        Webpack разбивает наше приложение. Он смотрит на эти импорты и генерирует необходимые чанки (куски, chunks).{' '}
      </p>
      <p className={styles.description}>
        После создания <code className={styles.code}>AsyncHome</code> мы можем использовать его в маршрутах. При
        совпадении маршрута, React Router создаст экземпляр компонента <code className={styles.code}>AsyncHome</code>,
        который, в свою очередь, динамически импортирует компонент <code className={styles.code}>Home</code> и
        отрендерит его.
      </p>
      <CopyBlock
        text={`<Route path="/" exact component={AsyncHome} />`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description} />
      <iframe
        height="300"
        scrolling="no"
        title="rotman_react_8_2"
        src="https://codesandbox.io/embed/kk78lp76m5?fontsize=14&hidenavigation=1&theme=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <img className={styles.img} src={splitImg} alt="split" />
      <p className={styles.description}>
        Каждый из файлов <code className={styles.code}>.chunk.js</code> - это вызовы{' '}
        <code className={styles.code}>import()</code>, асинхронно загружаемые компоненты.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Имена чанков</h3>
      <p className={styles.description}>Если необходимо дать чанкам вменяемые имена, делается это так: </p>
      <CopyBlock
        text={`const AsyncHome = asyncComponent({
  loader: () => import('./pages/Home' /* webpackChunkName: "home-page" */),
  loading: Loader,
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://github.com/webpack/webpack/tree/master/examples/code-splitting-specify-chunk-name"
          target="_blank"
          rel="noopener noreferrer"
        >
          code-splitting-specify-chunk-name
        </a>
      </p>
    </section>
  </section>
))
